import React, { useState } from "react";
import styles from "./LocationDrawer.module.css";

const sampleLocations = [
  { id: 2, name: "Mumbai", detail: "Maharashtra, India" },
  { id: 3, name: "Bengaluru", detail: "Karnataka, India" },
  { id: 4, name: "Delhi NCR", detail: "Delhi, India" },
  { id: 5, name: "Hyderabad", detail: "Telangana, India" },
  { id: 6, name: "Pune", detail: "Maharashtra, India" },
  { id: 7, name: "Chennai", detail: "Tamil Nadu, India" },
  { id: 8, name: "Kolkata", detail: "West Bengal, India" },
  { id: 9, name: "Ahmedabad", detail: "Gujarat, India" },
];

export default function LocationDrawer({
  isOpen = false,
  onClose,
  onLocationSelect,
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [loadingLocation, setLoadingLocation] = useState(false);

  const [liveLocation, setLiveLocation] = useState({
    latitude: null,
    longitude: null,
    address: "",
  });

  /* =========================================================
     FILTER LOCATIONS
  ========================================================= */

  const filteredLocations = sampleLocations.filter((loc) =>
    loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    loc.detail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* =========================================================
     GET ADDRESS FROM LATITUDE / LONGITUDE
  ========================================================= */

  const getAddressFromCoordinates = async (
    latitude,
    longitude
  ) => {
    try {
      /*
       * OpenStreetMap Nominatim
       * Reverse geocoding
       */

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Address API failed: ${response.status}`
        );
      }

      const data = await response.json();

      const address = data.display_name || "";

      return address;
    } catch (error) {
      console.error(
        "Address fetch error:",
        error
      );

      return "";
    }
  };

  /* =========================================================
     USE CURRENT LOCATION
  ========================================================= */

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert(
        "Your browser does not support location."
      );

      return;
    }

    setLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitude =
            position.coords.latitude;

          const longitude =
            position.coords.longitude;

          console.log(
            "Latitude:",
            latitude
          );

          console.log(
            "Longitude:",
            longitude
          );

          /*
           * Get actual address
           */

          const address =
            await getAddressFromCoordinates(
              latitude,
              longitude
            );

          console.log(
            "Current Address:",
            address
          );

          const locationData = {
            latitude,
            longitude,
            address:
              address ||
              `${latitude}, ${longitude}`,
          };

          /*
           * Save locally
           */

          localStorage.setItem(
            "live_location",
            JSON.stringify(locationData)
          );

          /*
           * Update drawer
           */

          setLiveLocation(
            locationData
          );

          /*
           * Send location to UserProfile
           */

          if (
            typeof onLocationSelect ===
            "function"
          ) {
            onLocationSelect(
              locationData
            );
          }

          /*
           * Close drawer
           */

          if (
            typeof onClose ===
            "function"
          ) {
            onClose();
          }
        } catch (error) {
          console.error(
            "Location processing error:",
            error
          );

          alert(
            "Location mil gayi, lekin address fetch nahi ho paya."
          );
        } finally {
          setLoadingLocation(false);
        }
      },

      (error) => {
        console.error(
          "Geolocation error:",
          error
        );

        setLoadingLocation(false);

        if (error.code === 1) {
          alert(
            "Location permission denied. Browser settings se location permission Allow karein."
          );
        } else if (error.code === 2) {
          alert(
            "Location available nahi hai. GPS/location service check karein."
          );
        } else if (error.code === 3) {
          alert(
            "Location request timeout ho gaya. Dobara try karein."
          );
        } else {
          alert(
            "Current location nahi mil paayi."
          );
        }
      },

      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  /* =========================================================
     SELECT MANUAL LOCATION
  ========================================================= */

  const handleManualLocation = (loc) => {
    setSelectedLocation(loc.id);

    const locationData = {
      latitude: null,
      longitude: null,
      address: `${loc.name}, ${loc.detail}`,
    };

    localStorage.setItem(
      "live_location",
      JSON.stringify(locationData)
    );

    if (
      typeof onLocationSelect ===
      "function"
    ) {
      onLocationSelect(
        locationData
      );
    }

    if (
      typeof onClose ===
      "function"
    ) {
      onClose();
    }
  };

  /* =========================================================
     IF DRAWER CLOSED
  ========================================================= */

  if (!isOpen) {
    return null;
  }

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div
      className={`${styles.drawerOverlay} ${
        isOpen ? styles.open : ""
      }`}
    >
      {/* BACKDROP */}

      <div
        className={styles.backdrop}
        onClick={onClose}
      />

      {/* DRAWER */}

      <div
        className={`${styles.drawerContainer} ${
          isExpanded
            ? styles.expanded
            : styles.collapsed
        }`}
      >
        {/* DRAG HANDLE */}

        <div
          className={
            styles.dragHandleWrapper
          }
          onClick={() =>
            setIsExpanded(
              !isExpanded
            )
          }
        >
          <div
            className={
              styles.dragHandleBar
            }
          />
        </div>

        {/* HEADER */}

        <header
          className={
            styles.drawerHeader
          }
        >
          <div
            className={
              styles.titleRow
            }
          >
            <h1
              className={
                styles.mainTitle
              }
            >
              Choose your location
            </h1>

            <button
              type="button"
              className={
                styles.toggleChevronBtn
              }
              onClick={() =>
                setIsExpanded(
                  !isExpanded
                )
              }
              aria-label="Toggle Location Drawer"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0A529B"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`${styles.chevronIcon} ${
                  isExpanded
                    ? styles.chevronUp
                    : styles.chevronDown
                }`}
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
          </div>

          <p
            className={
              styles.subTitle
            }
          >
            Select a location to see
            near work
          </p>
        </header>

        {/* CURRENT LOCATION */}

        <div
          className={
            styles.actionWrapper
          }
        >
          <button
            type="button"
            className={
              styles.currentLocationBtn
            }
            onClick={
              handleCurrentLocation
            }
            disabled={
              loadingLocation
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={
                styles.gpsTargetIcon
              }
            >
              <circle
                cx="12"
                cy="12"
                r="3"
              />

              <circle
                cx="12"
                cy="12"
                r="8"
              />

              <line
                x1="12"
                y1="2"
                x2="12"
                y2="4"
              />

              <line
                x1="12"
                y1="20"
                x2="12"
                y2="22"
              />

              <line
                x1="2"
                y1="12"
                x2="4"
                y2="12"
              />

              <line
                x1="20"
                y1="12"
                x2="22"
                y2="12"
              />
            </svg>

            <span>
              {loadingLocation
                ? "Getting your location..."
                : "Use my current location"}
            </span>
          </button>
        </div>

        {/* SHOW LIVE ADDRESS */}

        {liveLocation.address && (
          <div
            style={{
              margin: "0 16px 12px",
              padding: "12px",
              borderRadius: "12px",
              background: "#f1f7fc",
              color: "#005ca9",
              fontSize: "13px",
              lineHeight: "1.4",
            }}
          >
            <strong>
              Current location
            </strong>

            <div
              style={{
                marginTop: "4px",
                color: "#555",
              }}
            >
              {liveLocation.address}
            </div>
          </div>
        )}

        {/* SCROLL CONTENT */}

        <div
          className={
            styles.scrollContent
          }
        >
          {/* SEARCH */}

          <div
            className={
              styles.searchBoxWrapper
            }
          >
            <svg
              className={
                styles.searchIcon
              }
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8E8E93"
              strokeWidth="2"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />

              <line
                x1="21"
                y1="21"
                x2="16.65"
                y2="16.65"
              />
            </svg>

            <input
              type="text"
              placeholder="Search city or area..."
              value={searchQuery}
              onChange={(e) =>
                setSearchQuery(
                  e.target.value
                )
              }
              className={
                styles.searchInput
              }
            />
          </div>

          {/* LOCATION LIST */}

          <div
            className={
              styles.locationList
            }
          >
            {filteredLocations.map(
              (loc) => (
                <button
                  key={loc.id}
                  type="button"
                  className={`${styles.locationCard} ${
                    selectedLocation ===
                    loc.id
                      ? styles.selectedCard
                      : ""
                  }`}
                  onClick={() =>
                    handleManualLocation(
                      loc
                    )
                  }
                >
                  <div
                    className={
                      styles.locationIconBox
                    }
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#FF6B00"
                      strokeWidth="2"
                    >
                      <path d="M12 21s-6-5.333-6-10a6 6 0 0 1 12 0c0 4.667-6 10-6 10z" />

                      <circle
                        cx="12"
                        cy="11"
                        r="2"
                      />
                    </svg>
                  </div>

                  <div
                    className={
                      styles.locationInfo
                    }
                  >
                    <span
                      className={
                        styles.locationName
                      }
                    >
                      {loc.name}
                    </span>

                    <span
                      className={
                        styles.locationDetail
                      }
                    >
                      {loc.detail}
                    </span>
                  </div>

                  {selectedLocation ===
                    loc.id && (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#FF6B00"
                      strokeWidth="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}